/**
 * CIS Status column for employer dashboard → subcontractors list.
 * Derives labels from relationship + subcontractor payload (matches API relationships list).
 *
 * "UnmatchedTaxRateIndicator = yes" (HMRC no-match / higher unverified path) is represented by
 * relationship hmrc_verification_status values like NO_MATCH / UNVERIFIED.
 */

export function cisRateFromSubcontractorRow(sub) {
  const n = (v) => (v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null);
  return (
    n(sub.tax_rate) ??
    n(sub.subcontractor_cis_rate) ??
    null
  );
}

/** True if this employer–sub relationship has completed an HMRC verification call (any outcome). */
export function relationshipCisVerificationAttempted(sub) {
  const ref = (sub.hmrc_verification_ref || sub.subcontractor_hmrc_verification_ref || '')
    .toString()
    .trim();
  if (ref) return true;
  if (sub.hmrc_verified_at != null && String(sub.hmrc_verified_at).trim() !== '') return true;
  const hasRate =
    sub.tax_rate !== null && sub.tax_rate !== undefined && sub.tax_rate !== '';
  if (hasRate) return true;
  const vs = (sub.hmrc_verification_status != null ? String(sub.hmrc_verification_status).trim().toUpperCase() : '');
  if (!vs || vs === 'UNVERIFIED') return false;
  return true;
}

export function relationshipCisVerificationMatched(sub) {
  const status = String(sub?.hmrc_verification_status || '').trim().toUpperCase();
  if (!status) return false;
  return !['UNVERIFIED', 'NO_MATCH', 'NOT_MATCHED', 'NOT_VERIFIED'].includes(status);
}

/**
 * @returns {{ text: string, badgeClass: string }}
 */
export function getEmployerSubcontractorCisColumnPresentation(sub) {
  const hmrcVerified = relationshipCisVerificationMatched(sub);
  const attempted = relationshipCisVerificationAttempted(sub);
  const rate = cisRateFromSubcontractorRow(sub) ?? 30;

  if (!attempted) {
    return { text: '[Unverified]', badgeClass: 'status-badge status-unverified' };
  }

  if (!hmrcVerified) {
    const displayRate = cisRateFromSubcontractorRow(sub) ?? 30;
    return {
      text: `[Verification Attempted - NO MATCH (${displayRate}%)]`,
      badgeClass: 'status-badge status-cis-no-match',
    };
  }

  // Matched: UnmatchedTaxRateIndicator = no
  if (rate === 0) {
    return { text: '[Verified - Gross (0%)]', badgeClass: 'status-badge status-gross' };
  }
  if (rate === 20) {
    return { text: '[Verified - Net (20%)]', badgeClass: 'status-badge status-net' };
  }
  if (rate === 30) {
    return { text: '[Verified - Higher (30%)]', badgeClass: 'status-badge status-cis-verified-higher' };
  }
  return {
    text: `[Verified - Higher (${rate}%)]`,
    badgeClass: 'status-badge status-cis-verified-higher',
  };
}

/**
 * Employer subcontractors table: separate Status (rate / gross / unverified) and Match columns.
 * @returns {{ statusLabel: string, matchLabel: string, statusBadgeClass: string }}
 */
export function getEmployerSubcontractorStatusMatchPresentation(sub) {
  const attempted = relationshipCisVerificationAttempted(sub);
  const hmrcVerified = relationshipCisVerificationMatched(sub);
  const rate = cisRateFromSubcontractorRow(sub);

  if (!attempted) {
    return {
      statusLabel: 'Unverified',
      matchLabel: '',
      statusBadgeClass: 'status-badge status-unverified',
    };
  }

  if (!hmrcVerified) {
    const displayRate = rate ?? 30;
    return {
      statusLabel: `${displayRate}%`,
      matchLabel: 'Unmatched',
      statusBadgeClass: 'status-badge status-cis-no-match',
    };
  }

  const r = rate ?? 30;
  let statusLabel;
  let statusBadgeClass;
  if (r === 0) {
    statusLabel = 'Gross';
    statusBadgeClass = 'status-badge status-gross';
  } else if (r === 20) {
    statusLabel = '20%';
    statusBadgeClass = 'status-badge status-net';
  } else if (r === 30) {
    statusLabel = '30%';
    statusBadgeClass = 'status-badge status-cis-verified-higher';
  } else {
    statusLabel = `${r}%`;
    statusBadgeClass = 'status-badge status-cis-verified-higher';
  }

  return {
    statusLabel,
    matchLabel: 'Matched',
    statusBadgeClass,
  };
}
